/** Formatting utility functions. */

export function formatPrice(price: number | null | undefined): string {
  if (price === null || price === undefined || isNaN(price)) {
    return 'N/A';
  }
  // Format with thousand separators
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);
}

export function formatPercent(percent: number | null | undefined): string {
  if (percent === null || percent === undefined || isNaN(percent)) {
    return 'N/A';
  }
  const sign = percent >= 0 ? '+' : '';
  return `${sign}${percent.toFixed(2)}%`;
}

export function formatLargeNumber(num: number | null | undefined): string {
  if (num === null || num === undefined || isNaN(num)) {
    return 'N/A';
  }
  if (num >= 1e9) {
    return `${(num / 1e9).toFixed(2)}B`;
  }
  if (num >= 1e6) {
    return `${(num / 1e6).toFixed(2)}M`;
  }
  if (num >= 1e3) {
    return `${(num / 1e3).toFixed(2)}K`;
  }
  return num.toFixed(0);
}

/** USD-style compact amounts with $ prefix (market cap, float, etc.). */
export function formatUsdCompact(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) {
    return 'N/A';
  }
  if (value >= 1e12) {
    return `$${(value / 1e12).toFixed(2)}T`;
  }
  if (value >= 1e9) {
    return `$${(value / 1e9).toFixed(2)}B`;
  }
  if (value >= 1e6) {
    return `$${(value / 1e6).toFixed(2)}M`;
  }
  if (value >= 1e3) {
    return `$${(value / 1e3).toFixed(2)}K`;
  }
  return `$${value.toFixed(2)}`;
}

/** Share volume without currency prefix (B / M / K or locale string). */
export function formatVolumeCompact(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) {
    return 'N/A';
  }
  if (value >= 1e9) {
    return `${(value / 1e9).toFixed(2)}B`;
  }
  if (value >= 1e6) {
    return `${(value / 1e6).toFixed(2)}M`;
  }
  if (value >= 1e3) {
    return `${(value / 1e3).toFixed(2)}K`;
  }
  return value.toLocaleString();
}

/** Percent from a 0–1 ratio (e.g. short interest as 0.15 → "15.00%"). */
export function formatRatioAsPercent(ratio: number | null | undefined): string {
  if (ratio === null || ratio === undefined || isNaN(ratio)) {
    return 'N/A';
  }
  return `${(ratio * 100).toFixed(2)}%`;
}

export function formatSentiment(sentiment: number | null | undefined): string {
  if (sentiment === null || sentiment === undefined || isNaN(sentiment)) {
    return 'N/A';
  }
  return sentiment.toFixed(2);
}

export function formatTimestamp(timestamp: string): string {
  try {
    // Parse as UTC if it's a string (API sends UTC timestamps)
    // If string has 'Z' or '+00:00', it's already UTC
    // Otherwise, assume it's UTC and parse accordingly
    const timestampStr = String(timestamp);
    let date: Date;
    
    if (timestampStr.includes('Z') || timestampStr.includes('+00:00')) {
      date = new Date(timestampStr);
    } else if (timestampStr.includes('T')) {
      date = new Date(timestampStr.endsWith('Z') ? timestampStr : `${timestampStr}Z`);
    } else {
      // SQL-style "YYYY-MM-DD HH:MM:SS" from API — treat as UTC
      date = new Date(`${timestampStr.replace(' ', 'T')}Z`);
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid timestamp:', timestamp, 'parsed as:', date);
      return timestamp;
    }
    
    // Format in EST/EDT (America/New_York)
    // Use Intl.DateTimeFormat for reliable timezone conversion
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
    
    return formatter.format(date);
  } catch (error) {
    console.error('Error formatting timestamp:', error, timestamp);
    return timestamp;
  }
}

export function escapeHTML(text: string | null | undefined): string {
  if (!text) {
    return '';
  }
  // Use textContent to escape HTML, but we need to manually escape quotes
  const div = document.createElement('div');
  div.textContent = text;
  // Manually escape quotes since textContent doesn't escape them
  return div.innerHTML
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Normalize Reddit URL to ensure it's an absolute URL.
 * Handles cases where URLs might be relative or missing protocol.
 */
export function normalizeRedditUrl(url: string | null | undefined): string {
  if (!url) {
    return '#';
  }
  
  const urlStr = String(url).trim();
  
  // If already an absolute URL, return as is
  if (urlStr.startsWith('http://') || urlStr.startsWith('https://')) {
    return urlStr;
  }
  
  // If it starts with reddit.com or www.reddit.com, add https://
  if (urlStr.startsWith('reddit.com') || urlStr.startsWith('www.reddit.com')) {
    return `https://${urlStr}`;
  }
  
  // If it starts with /r/ or /u/ or /comments/, it's a Reddit permalink
  if (urlStr.startsWith('/r/') || urlStr.startsWith('/u/') || urlStr.startsWith('/comments/')) {
    return `https://reddit.com${urlStr}`;
  }
  
  // Fix common typos like "https.reddit.com" -> "https://reddit.com"
  if (urlStr.startsWith('https.reddit.com')) {
    return urlStr.replace('https.reddit.com', 'https://reddit.com');
  }
  
  if (urlStr.startsWith('http.reddit.com')) {
    return urlStr.replace('http.reddit.com', 'https://reddit.com');
  }
  
  // If it doesn't start with /, assume it's a full Reddit URL missing protocol
  if (!urlStr.startsWith('/')) {
    return `https://${urlStr}`;
  }
  
  // Default: treat as Reddit permalink
  return `https://reddit.com${urlStr}`;
}

/**
 * Normalize ticker symbol for consistent database queries.
 * Strips symbols, converts to uppercase, and handles edge cases.
 */
export function normalizeTicker(ticker: string | null | undefined): string {
  if (!ticker) {
    return '';
  }
  
  const tickerStr = String(ticker).trim();
  
  // Remove common symbols ($, @, etc.) and convert to uppercase
  const normalized = tickerStr
    .replace(/[^a-zA-Z0-9.]/g, '') // Remove all non-alphanumeric except dots
    .toUpperCase();
  
  return normalized;
}

